/**
 * Frontend service for the Säljverktyg (Sales Tool) page.
 * Combines sales-tool-specific endpoints (company lookup, supplier stats)
 * with reusable procurement-stats endpoints (category data, trends, geography).
 */
import axios from "axios";
import BaseService from "./baseService";

class ServiceSalesToolController extends BaseService {
  // --- Sales Tool endpoints (company-specific) ---

  async companyLookup(query: string) {
    const response = await axios.get("/api/sales-tool/company-lookup", {
      params: { q: query },
    });
    return response.data;
  }

  async getSupplierStats(organizationNumber: string) {
    const response = await axios.get("/api/sales-tool/supplier-stats", {
      params: { organizationNumber },
    });
    return response.data;
  }

  async getSupplierUsers(organizationNumber: string) {
    const response = await axios.get("/api/sales-tool/supplier-users", {
      params: { organizationNumber },
    });
    return response.data;
  }

  async getSupplierReach(organizationNumber: string, areaIds: string[]) {
    const response = await axios.get("/api/sales-tool/supplier-reach", {
      params: { organizationNumber, areaIds: areaIds.join(",") },
    });
    return response.data;
  }

  async getROIData(organizationNumber: string, areaIds: string[]) {
    const response = await axios.get("/api/sales-tool/roi-data", {
      params: { organizationNumber, areaIds: areaIds.join(",") },
    });
    return response.data;
  }

  async getBenchmark(organizationNumber: string) {
    const response = await axios.get("/api/sales-tool/benchmark", {
      params: { organizationNumber },
    });
    return response.data;
  }

  /** List all active organizations for the search dropdown */
  async getOrganizations() {
    const response = await axios.get("/api/sales-tool/organizations");
    return response.data;
  }

  /** Superadmin's global categories with customerAreas mappings */
  async getGlobalCategories() {
    const response = await axios.get("/api/sales-tool/global-categories");
    return response.data;
  }

  // --- Procurement Stats endpoints (platform-wide, reusable) ---

  async getCategoryStats(areaIds: string[], places?: string[]) {
    const response = await axios.get("/api/procurement-stats/by-category", {
      params: {
        areaIds: areaIds.join(","),
        ...(places && { places: places.join(",") }),
      },
    });
    return response.data;
  }

  async getCategoryStatsCombined(areaIds: string[], options?: {
    places?: string[];
    fromDate?: string;
    toDate?: string;
    ongoingOnly?: boolean;
  }) {
    const response = await axios.get("/api/procurement-stats/by-category-combined", {
      params: {
        areaIds: areaIds.join(","),
        ...(options?.ongoingOnly && { ongoingOnly: 'true' }),
        ...(options?.places && { places: options.places.join(",") }),
        ...(options?.fromDate && { fromDate: options.fromDate }),
        ...(options?.toDate && { toDate: options.toDate }),
      },
    });
    return response.data;
  }

  async getTopCategories(options?: {
    limit?: number;
    places?: string[];
    isPublished?: boolean;
  }) {
    const response = await axios.get("/api/procurement-stats/top-categories", {
      params: {
        ...(options?.limit && { limit: options.limit }),
        ...(options?.places && { places: options.places.join(",") }),
        ...(options?.isPublished !== undefined && { isPublished: options.isPublished }),
      },
    });
    return response.data;
  }

  async getPlatformTotals() {
    const response = await axios.get("/api/procurement-stats/totals");
    return response.data;
  }

  async getGeography() {
    const response = await axios.get("/api/procurement-stats/geography");
    return response.data;
  }

  async getTrend(options?: {
    areaIds?: string[];
    period?: "monthly" | "yearly";
    places?: string[];
    isPublished?: boolean;
  }) {
    const response = await axios.get("/api/procurement-stats/trend", {
      params: {
        ...(options?.areaIds && { areaIds: options.areaIds.join(",") }),
        ...(options?.period && { period: options.period }),
        ...(options?.places && { places: options.places.join(",") }),
        ...(options?.isPublished !== undefined && { isPublished: options.isPublished }),
      },
    });
    return response.data;
  }
}

export const ServiceSalesTool = new ServiceSalesToolController();
